import {getActiveDocument} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {toPoint, type Point} from '@packages/systems/open-canvas/core';

/**
 * The attribute to be added to the Frame's DOM element
 */
export const WF_FRAME_ID_ATTRIBUTE = 'data-wf-frame-id';

/**
 * Given a frame id, returns the DOM element of the frame
 */
export const getFrameDOMElementById = (id: string): HTMLElement | null => {
  // Note: getActiveDocument will throw if an iframe is not found.
  const doc = getActiveDocument();
  if (!doc) {
    return null;
  }

  return doc.querySelector(`[${WF_FRAME_ID_ATTRIBUTE}="${id}"]`);
};

/**
 * Returns the center point of a Frame's DOM element in screen space.
 *
 * Important: this uses `getBoundingClientRect()`, which returns dimensions in viewport/screen
 * coordinates after any CSS transforms are applied (including current canvas pan/zoom scaling).
 */
export const getFrameElementCenter = (frameId: string): Point | null => {
  const frameElement = getFrameDOMElementById(frameId);
  if (!frameElement) return null;

  const rect = frameElement.getBoundingClientRect();
  return toPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
};

type MeasuredFrameDimensions = {
  width: number;
  height: number;
};

/**
 * Asynchronously measures the provided frame element and returns it's dimensions in world units.
 * This function uses a `ResizeObserver` to measure the frame element asynchronously.
 *
 * Unlike how `getBoundingClientRect` works, this method of measuring returns the
 * dimensions of the element without any of the scale transformations applied.
 *
 * @param frameId - The id of the frame to measure. This function will call `getFrameDOMElementById` to get the frame's DOM element.
 * @returns A promise that resolves to the measured dimensions of the frame element, or `null` if the frame element cannot be found.
 */
export async function getFrameDimensions(frameId: string) {
  const frameElement = getFrameDOMElementById(frameId);

  if (!frameElement) return Promise.resolve(null);

  const promise = new Promise<MeasuredFrameDimensions | null>((resolve) => {
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        const {width, height} = entry.contentRect;
        resolve({width, height});
      } else {
        resolve(null);
      }

      resizeObserver.disconnect();
    });

    resizeObserver.observe(frameElement);
  });

  return promise;
}

/**
 * Given a native DOM node, returns the id of the closest Frame
 * @param nativeElement
 */
export const getClosestFrameId = (nativeElement: HTMLElement): string => {
  const closestFrame = nativeElement.closest(`[${WF_FRAME_ID_ATTRIBUTE}]`);
  if (!closestFrame) return '';

  const frameId = closestFrame.getAttribute(WF_FRAME_ID_ATTRIBUTE);
  return frameId || '';
};

type FrameDOMData = {
  /**
   * The id of the Frame
   */
  id: string;
  /**
   * The DOM element of the Frame
   */
  element: HTMLElement;
};

/**
 * Given a native DOM node, returns the id and the DOM element of the closest Frame
 * @param nativeElement
 * @returns `FrameDOMData` containing the id and the DOM element of the closest Frame, or `null` if no Frame is found
 */
export const getClosestFrameDOMElement = (
  nativeElement: HTMLElement
): FrameDOMData | null => {
  const closestFrame = nativeElement.closest(
    `[${WF_FRAME_ID_ATTRIBUTE}]`
  ) as HTMLElement;
  const frameId = closestFrame?.getAttribute(WF_FRAME_ID_ATTRIBUTE);

  if (frameId && closestFrame) {
    return {
      id: frameId,
      element: closestFrame,
    };
  }

  return null;
};

/**
 * Returns true if the provided `nativeElement` is a Frame DOM element.
 */
const isFrameDOMElement = (nativeElement: HTMLElement | null): boolean => {
  if (!nativeElement) return false;

  return nativeElement.hasAttribute(WF_FRAME_ID_ATTRIBUTE);
};

/**
 * Returns true if the provided `nativeElement` is a descendant of a Frame element in the DOM.
 * If the provided element is the Frame itself this function will return false, as Frame DOM elements
 * should not be selectable on the Canvas.
 * @param nativeElement
 */
export const isDOMElementWithinFrame = (
  nativeElement: HTMLElement | null
): boolean => {
  if (!nativeElement) return false;

  if (isFrameDOMElement(nativeElement)) return false;

  const closestFrameId = getClosestFrameId(nativeElement);

  return Boolean(closestFrameId);
};
